/**
 * Real-Check theme catalog.
 *
 * Each theme is defined as HSL values (as strings, matching Tailwind CSS-variable
 * convention) so they can be pasted directly into --background style props.
 *
 * All themes have been designed to avoid the "AI slop" purple-on-white default.
 * Each has a distinct emotional register and use case.
 */

export const THEMES = {
    midnight: {
        id: "midnight",
        name: "Midnight Compass",
        tagline: "The founding aesthetic — premium, calm, focused.",
        mode: "dark",
        vibe: ["premium", "focused", "night"],
        vars: {
            background: "235 77% 8%",
            foreground: "47 100% 92%",
            card: "235 50% 12%",
            "card-foreground": "47 100% 92%",
            popover: "235 50% 12%",
            "popover-foreground": "47 100% 92%",
            primary: "246 74% 53%",
            "primary-foreground": "0 0% 100%",
            secondary: "235 30% 20%",
            "secondary-foreground": "47 100% 92%",
            muted: "235 30% 20%",
            "muted-foreground": "235 20% 70%",
            accent: "47 100% 92%",
            "accent-foreground": "235 77% 8%",
            destructive: "0 84% 60%",
            "destructive-foreground": "0 0% 100%",
            border: "235 40% 16%",
            input: "235 40% 16%",
            ring: "246 74% 53%",
            success: "142 71% 45%",
            "success-foreground": "0 0% 100%",
            warning: "38 92% 50%",
            "warning-foreground": "0 0% 10%",
        },
        // extras used in inline background gradients / dots
        heroGlow: "radial-gradient(circle at 20% -10%, rgba(61, 44, 224, 0.28), transparent 55%), radial-gradient(circle at 85% 10%, rgba(120, 91, 255, 0.14), transparent 45%), radial-gradient(circle at 50% 100%, rgba(255, 246, 214, 0.05), transparent 40%)",
        preview: { bg: "#050826", primary: "#3D2CE0", accent: "#FFF6D6" },
    },

    aurora: {
        id: "aurora",
        name: "Aurora Truth",
        tagline: "Northern-lights teal + electric emerald. Hopeful, uncommon.",
        mode: "dark",
        vibe: ["hopeful", "unique", "bright"],
        vars: {
            background: "180 65% 5%",
            foreground: "45 100% 94%",
            card: "180 45% 9%",
            "card-foreground": "45 100% 94%",
            popover: "180 45% 9%",
            "popover-foreground": "45 100% 94%",
            primary: "165 90% 42%",
            "primary-foreground": "180 65% 5%",
            secondary: "180 30% 17%",
            "secondary-foreground": "45 100% 94%",
            muted: "180 25% 17%",
            "muted-foreground": "180 15% 68%",
            accent: "45 100% 94%",
            "accent-foreground": "180 65% 5%",
            destructive: "340 90% 60%",
            "destructive-foreground": "0 0% 100%",
            border: "180 35% 14%",
            input: "180 35% 14%",
            ring: "165 90% 42%",
            success: "150 80% 45%",
            "success-foreground": "0 0% 100%",
            warning: "38 92% 50%",
            "warning-foreground": "0 0% 10%",
        },
        heroGlow: "radial-gradient(circle at 15% 0%, rgba(16, 200, 160, 0.35), transparent 55%), radial-gradient(circle at 90% 20%, rgba(180, 120, 255, 0.14), transparent 45%), radial-gradient(circle at 60% 100%, rgba(255, 240, 200, 0.06), transparent 40%)",
        preview: { bg: "#04211f", primary: "#12C7A0", accent: "#FFF6D6" },
    },

    copper: {
        id: "copper",
        name: "Copper & Ink",
        tagline: "Editorial gravitas — newspaper you'd actually read.",
        mode: "dark",
        vibe: ["editorial", "serious", "warm"],
        vars: {
            background: "24 40% 6%",
            foreground: "40 45% 92%",
            card: "24 30% 10%",
            "card-foreground": "40 45% 92%",
            popover: "24 30% 10%",
            "popover-foreground": "40 45% 92%",
            primary: "22 90% 55%",
            "primary-foreground": "24 40% 6%",
            secondary: "24 20% 18%",
            "secondary-foreground": "40 45% 92%",
            muted: "24 20% 18%",
            "muted-foreground": "24 15% 68%",
            accent: "40 45% 92%",
            "accent-foreground": "24 40% 6%",
            destructive: "0 84% 55%",
            "destructive-foreground": "0 0% 100%",
            border: "24 25% 15%",
            input: "24 25% 15%",
            ring: "22 90% 55%",
            success: "142 55% 45%",
            "success-foreground": "0 0% 100%",
            warning: "38 92% 55%",
            "warning-foreground": "0 0% 10%",
        },
        heroGlow: "radial-gradient(circle at 20% 0%, rgba(230, 130, 40, 0.28), transparent 55%), radial-gradient(circle at 88% 15%, rgba(200, 100, 30, 0.12), transparent 45%)",
        preview: { bg: "#1a0f08", primary: "#E67E22", accent: "#F2E8D5" },
    },

    sunrise: {
        id: "sunrise",
        name: "Sunrise Hope",
        tagline: "Light mode. Warm, welcoming, compassionate.",
        mode: "light",
        vibe: ["light", "warm", "welcoming"],
        vars: {
            background: "40 55% 96%",
            foreground: "235 55% 14%",
            card: "40 40% 99%",
            "card-foreground": "235 55% 14%",
            popover: "40 40% 99%",
            "popover-foreground": "235 55% 14%",
            primary: "12 85% 52%",
            "primary-foreground": "40 55% 96%",
            secondary: "40 30% 90%",
            "secondary-foreground": "235 55% 14%",
            muted: "40 25% 90%",
            "muted-foreground": "235 20% 40%",
            accent: "235 55% 14%",
            "accent-foreground": "40 55% 96%",
            destructive: "0 84% 50%",
            "destructive-foreground": "0 0% 100%",
            border: "40 25% 85%",
            input: "40 25% 88%",
            ring: "12 85% 52%",
            success: "142 65% 40%",
            "success-foreground": "0 0% 100%",
            warning: "38 92% 45%",
            "warning-foreground": "0 0% 10%",
        },
        heroGlow: "radial-gradient(circle at 20% -10%, rgba(255, 138, 76, 0.20), transparent 55%), radial-gradient(circle at 88% 15%, rgba(255, 200, 100, 0.16), transparent 45%)",
        preview: { bg: "#FBF3E1", primary: "#E85A2C", accent: "#0F1738" },
    },

    terminal: {
        id: "terminal",
        name: "Terminal Truth",
        tagline: "Investigator mode. True-black, electric green, amber accents.",
        mode: "dark",
        vibe: ["hacker", "investigative", "sharp"],
        vars: {
            background: "0 0% 3%",
            foreground: "60 30% 90%",
            card: "0 0% 6%",
            "card-foreground": "60 30% 90%",
            popover: "0 0% 6%",
            "popover-foreground": "60 30% 90%",
            primary: "142 100% 45%",
            "primary-foreground": "0 0% 3%",
            secondary: "0 0% 12%",
            "secondary-foreground": "60 30% 90%",
            muted: "0 0% 12%",
            "muted-foreground": "60 8% 65%",
            accent: "45 100% 62%",
            "accent-foreground": "0 0% 3%",
            destructive: "0 100% 55%",
            "destructive-foreground": "0 0% 100%",
            border: "0 0% 15%",
            input: "0 0% 12%",
            ring: "142 100% 45%",
            success: "142 100% 45%",
            "success-foreground": "0 0% 3%",
            warning: "45 100% 55%",
            "warning-foreground": "0 0% 3%",
        },
        heroGlow: "radial-gradient(circle at 15% 0%, rgba(0, 230, 100, 0.16), transparent 55%), radial-gradient(circle at 90% 20%, rgba(255, 200, 60, 0.08), transparent 45%)",
        preview: { bg: "#080808", primary: "#00E664", accent: "#FFC93C" },
    },

    rose: {
        id: "rose",
        name: "Rose Quartz",
        tagline: "Soft, human, safe — 'a place to breathe.'",
        mode: "dark",
        vibe: ["soft", "compassionate", "human"],
        vars: {
            background: "340 25% 8%",
            foreground: "20 55% 94%",
            card: "340 20% 12%",
            "card-foreground": "20 55% 94%",
            popover: "340 20% 12%",
            "popover-foreground": "20 55% 94%",
            primary: "335 65% 62%",
            "primary-foreground": "340 25% 8%",
            secondary: "340 15% 20%",
            "secondary-foreground": "20 55% 94%",
            muted: "340 15% 20%",
            "muted-foreground": "340 15% 70%",
            accent: "20 55% 94%",
            "accent-foreground": "340 25% 8%",
            destructive: "0 84% 60%",
            "destructive-foreground": "0 0% 100%",
            border: "340 20% 16%",
            input: "340 20% 16%",
            ring: "335 65% 62%",
            success: "150 55% 50%",
            "success-foreground": "0 0% 100%",
            warning: "38 85% 55%",
            "warning-foreground": "0 0% 10%",
        },
        heroGlow: "radial-gradient(circle at 20% 0%, rgba(220, 120, 160, 0.28), transparent 55%), radial-gradient(circle at 88% 20%, rgba(255, 180, 200, 0.12), transparent 45%), radial-gradient(circle at 60% 100%, rgba(255, 220, 210, 0.06), transparent 40%)",
        preview: { bg: "#1c1013", primary: "#D97AA1", accent: "#F7E5DD" },
    },
};

export const LAYOUTS = {
    focused: {
        id: "focused",
        name: "Focused Hero",
        tagline: "Single centered column. Search + verdict = the whole event.",
    },
    split: {
        id: "split",
        name: "Split Command",
        tagline: "Search on the left, live Fact of the Day + trending on the right.",
    },
};

export const DEFAULT_THEME = "aurora";
export const DEFAULT_LAYOUT = "focused";
